import { useEffect, useState } from 'react';
import { ArrowRight, Check, ChevronLeft, Link2, Plus, Search, Truck, X } from 'lucide-react';
import Sheet from './Sheet';
import ProductImage from './ProductImage';
import { api, errorMessage } from '../lib/api';
import { boxDims, boxLabel, isMultiBox, lineMatches } from '../lib/boxes';
import { cn, fmtDate, qty } from '../lib/utils';

/* Shown when a scan does not match the open order. Looks the code up
   globally, offers the other orders it belongs to, or lets the operator
   attach it to a line (and, for multi-box products, to a specific box) so
   the next scan resolves. */
/* full: { line, entry } when the code belongs to this receipt but every line
   it fits is already complete. Then nothing is counted; the sheet offers the
   other open orders that still expect the product, or counting it anyway. */
export default function UnknownSheet({ code, order, full = null, onClose, onJump, onLinked, onAddBox, onCount, onCountAnyway }) {
  const [state, setState] = useState({ loading: true });
  const [linking, setLinking] = useState(false);
  const [pickBoxFor, setPickBoxFor] = useState(null);
  const [busy, setBusy] = useState(null);
  const [find, setFind] = useState(''); // narrow the line list by name, vendor code, EAN or PO
  const [replace, setReplace] = useState(null); // { line, box, current }: the box already carries another code

  useEffect(() => {
    let alive = true;
    setState({ loading: true });
    api
      .lookup(code)
      .then((data) => alive && setState({ loading: false, data }))
      .catch((err) => alive && setState({ loading: false, error: errorMessage(err) }));
    return () => {
      alive = false;
    };
  }, [code]);

  const product = state.data?.products?.[0];
  const doneLine = product && order?.lines.find((l) => l.product_id === product.id && l.status === 'done');
  const selected = new Set(order?.ids || (order?.id ? [order.id] : []));
  const otherOrders = (state.data?.orders || []).filter((o) => !selected.has(o.id));
  const openLines = (order?.lines || []).filter((l) => l.status === 'open');
  // The code is not on any box of this order, but Odoo knows it (the product's
  // own EAN, a vendor code…) for a product this order does expect: say so,
  // and let the person count it here or link the code so the next scan matches.
  const foundIds = new Set((state.data?.products || []).map((p) => p.id));
  const hereLines = state.loading || full ? [] : openLines.filter((l) => foundIds.has(l.product_id));
  const shownLines = find.trim() ? openLines.filter((l) => lineMatches(l, find)) : openLines;

  const link = async (line, box) => {
    if (isMultiBox(line) && !box) {
      setPickBoxFor(line);
      return;
    }
    if (!box && line.boxes.length === 0) {
      onAddBox?.(line, code); // no box on file yet: capture size and weight along with the code
      return;
    }
    setBusy(box ? box.id : line.id);
    try {
      const res = await api.linkBarcode(line.product_id, code, box ? box.id : null, '', Boolean(replace));
      onLinked?.(line, code, res.box_id || (box ? box.id : null));
    } catch (err) {
      const conflict = err?.response?.data?.conflict;
      if (conflict && !replace) {
        // the box already carries a code: ask, then link again with replace
        setReplace({ line, box: box || line.boxes.find((b) => b.id === conflict.box_id) || null, current: conflict.current });
        setState((s) => ({ ...s, error: null }));
      } else {
        setState((s) => ({ ...s, error: errorMessage(err) }));
      }
      setBusy(null);
    }
  };

  const confirmReplace = async () => {
    const { line, box } = replace;
    setBusy(box ? box.id : line.id);
    try {
      const res = await api.linkBarcode(line.product_id, code, box ? box.id : null, '', true);
      setReplace(null);
      onLinked?.(line, code, res.box_id || (box ? box.id : null));
    } catch (err) {
      setState((s) => ({ ...s, error: errorMessage(err) }));
      setBusy(null);
    }
  };

  return (
    <Sheet open onClose={onClose} label="Barcode not on this order">
      <div className="px-4 pb-4 pt-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className={cn('font-cond text-[24px] font-bold leading-none', hereLines.length || full ? 'text-out' : 'text-alert')}>{full ? 'Already complete' : hereLines.length ? 'On this order' : 'Not on this order'}</p>
            <p className="mt-1 font-mono text-[15px] tabular-nums text-ink2">{code}</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="-mr-2 -mt-1 flex h-10 w-10 items-center justify-center rounded-full active:bg-sunken">
            <X className="h-5 w-5" />
          </button>
        </div>

        {state.loading && <p className="mt-4 text-[14px] text-ink2">Checking Odoo…</p>}
        {state.error && <p className="mt-4 rounded-xl bg-alert-bg px-3 py-2 text-[14px] text-alert">{state.error}</p>}
        {replace ? (
          <section className="mt-4" aria-label="Replace the EAN">
            <div className="tone-crossdock rounded-xl bg-tone-bg px-3 py-3">
              <p className="text-[15px] font-semibold text-tone">This box already carries another EAN</p>
              <p className="clamp-2 mt-1 text-[14px] font-semibold">{replace.line.name}</p>
              {replace.box && isMultiBox(replace.line) && <p className="text-[13px] text-ink2">{boxLabel(replace.box, replace.line.boxes.indexOf(replace.box))}</p>}
              <div className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-[14px]">
                <span className="text-ink2">Now</span>
                <span className="font-mono tabular-nums line-through decoration-ink2/60">{replace.current}</span>
                <span className="text-ink2">New</span>
                <span className="font-mono font-semibold tabular-nums">{code}</span>
              </div>
              <p className="mt-2 text-[12px] text-ink2">Replace it if the label changed. Cartons still printed with the old code will no longer scan.</p>
            </div>
            <button type="button" onClick={confirmReplace} disabled={busy !== null} className="mt-3 h-14 w-full rounded-xl bg-ink text-[16px] font-semibold text-bg active:opacity-80 disabled:opacity-50">
              {busy !== null ? 'Replacing…' : 'Replace the EAN'}
            </button>
            <button type="button" onClick={() => setReplace(null)} disabled={busy !== null} className="mt-2 h-12 w-full rounded-xl bg-sunken text-[15px] font-semibold active:bg-line">
              Keep the old one
            </button>
          </section>
        ) : (
          <>
        {!state.loading && product && (
          <div className="mt-4 flex items-center gap-3 rounded-xl bg-sunken p-3">
            <ProductImage src={product.image} alt="" className="h-14 w-14" />
            <div className="min-w-0">
              <p className="text-[15px] font-semibold leading-tight">{product.name}</p>
              <p className="font-mono text-[13px] text-ink2">{[product.vendor_code, product.barcode && `EAN ${product.barcode}`].filter(Boolean).join(' · ')}</p>
              {doneLine && <p className="mt-1 text-[13px] font-semibold text-out">Already fully received on this order.</p>}
            </div>
          </div>
        )}

        {full && (
          <section className="mt-4" aria-label="Already complete">
            <div className="rounded-xl border border-line px-3 py-2.5">
              <span className="clamp-2 block text-[14px] font-semibold leading-tight">{full.line.name}</span>
              <span className="block text-[12px] text-ink2">
                {full.line.po?.name ? `${full.line.po.name.split(' ')[0]} · ` : ''}all {qty(full.line.to_receive)} counted
              </span>
            </div>
            <p className="mt-2 text-[14px] text-ink2">
              This carton is one too many, so it was <span className="font-semibold text-ink">not counted</span>.
              {otherOrders.length > 0 ? ' Another open order still expects this product:' : state.loading ? '' : ' No other open order expects this product: set it aside.'}
            </p>
          </section>
        )}

        {hereLines.length > 0 && !linking && !pickBoxFor && (
          <section className="mt-4" aria-label="On this order">
            <p className="text-[14px] text-ink2">
              This code is not on the carton record yet, so the scan did not count. Count it by hand, or link the code once and every next scan will match.
            </p>
            <ul className="mt-2 space-y-2">
              {hereLines.map((l) => (
                <li key={l.id} className="rounded-xl border border-line px-3 py-2.5">
                  <span className="clamp-2 block text-[14px] font-semibold leading-tight">{l.name}</span>
                  <span className="block text-[12px] text-ink2">
                    {order?.multi && l.po?.name && `${l.po.name} · `}
                    {qty(l.to_receive)} to receive
                    {l.boxes.length === 0 && ' · no box on file yet'}
                  </span>
                  <span className="mt-2 flex gap-2">
                    <button type="button" onClick={() => onCount?.(l)} className="flex h-11 flex-1 items-center justify-center gap-1.5 rounded-xl bg-ink text-[14px] font-semibold text-bg active:opacity-80">
                      <Check className="h-4 w-4" /> Count it
                    </button>
                    <button type="button" disabled={busy !== null} onClick={() => link(l)} className="flex h-11 flex-1 items-center justify-center gap-1.5 rounded-xl bg-sunken text-[14px] font-semibold active:bg-line disabled:opacity-50">
                      <Link2 className="h-4 w-4" /> {busy === l.id ? 'Linking…' : 'Link this code'}
                    </button>
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {!state.loading && otherOrders.length > 0 && (
          <section className="mt-4">
            <h3 className="text-[13px] font-semibold text-ink2">{full ? 'Still open on' : hereLines.length ? 'Also expected on' : 'Expected on'}</h3>
            <ul className="mt-1 divide-y divide-line rounded-xl border border-line">
              {otherOrders.map((o) => (
                <li key={o.id} className="px-3 py-2.5">
                  <span className="flex items-center gap-3">
                    <span className="min-w-0 flex-1">
                      <span className="font-cond text-[20px] font-bold leading-none">{o.name}</span>
                      <span className="block truncate text-[13px] text-ink2">
                        {o.supplier} · {qty(o.to_receive)} to receive{o.expected && ` · ${fmtDate(o.expected)}`}
                      </span>
                    </span>
                    {o.shipped_pending && <Truck className="h-4 w-4 shrink-0" />}
                  </span>
                  <span className="mt-2 flex gap-2">
                    <button type="button" onClick={() => onJump?.(o, 'add')} className="flex h-11 flex-1 items-center justify-center gap-1.5 rounded-xl bg-ink text-[14px] font-semibold text-bg active:opacity-80">
                      <Plus className="h-4 w-4" /> Add to this delivery
                    </button>
                    <button type="button" onClick={() => onJump?.(o, 'alone')} className="flex h-11 items-center justify-center gap-1 rounded-xl bg-sunken px-3 text-[14px] font-semibold active:bg-line">
                      Open alone <ArrowRight className="h-4 w-4" />
                    </button>
                  </span>
                </li>
              ))}
            </ul>
            <p className="mt-1.5 text-[12px] text-ink2">Adding keeps every order and count you already have, and counts this carton on {otherOrders.length === 1 ? otherOrders[0].name : 'the order you add'}.</p>
          </section>
        )}

        {full && !state.loading && (
          <button type="button" onClick={() => onCountAnyway?.(full.line, full.entry)} className="mt-4 h-11 w-full rounded-xl bg-sunken text-[14px] font-semibold text-ink2 active:bg-line">
            Count it here anyway (+1 extra)
          </button>
        )}

        {!state.loading && product && !otherOrders.length && !doneLine && !hereLines.length && !full && (
          <p className="mt-3 text-[14px] text-ink2">Known product, but no open purchase order expects it.</p>
        )}
        {!state.loading && !product && !state.error && !linking && (
          <p className="mt-4 text-[14px] text-ink2">No product carries this barcode. If the carton belongs to a line of this order, link it once and every next scan will match.</p>
        )}

        {!state.loading && !product && openLines.length > 0 && !linking && (
          <button type="button" onClick={() => setLinking(true)} className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-ink py-3 text-[15px] font-semibold text-bg active:opacity-80">
            <Link2 className="h-4 w-4" /> Link barcode to a line
          </button>
        )}

        {linking && !pickBoxFor && (
          <section className="mt-4">
            <h3 className="text-[13px] font-semibold text-ink2">Which product is in this carton?</h3>
            {openLines.length > 5 && (
              <label className="mt-1.5 flex h-11 items-center gap-2 rounded-xl border border-line bg-surface px-3 focus-within:border-ink">
                <Search className="h-4 w-4 shrink-0 text-ink2" />
                <input
                  value={find}
                  onChange={(e) => setFind(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
                  placeholder={order?.multi ? 'Name, vendor code, EAN or PO' : 'Name, vendor code or EAN'}
                  inputMode="search"
                  autoComplete="off"
                  className="min-w-0 flex-1 bg-transparent text-[15px] outline-none placeholder:text-ink2/90"
                />
                {find && (
                  <button type="button" onClick={() => setFind('')} aria-label="Clear" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full active:bg-sunken">
                    <X className="h-4 w-4" />
                  </button>
                )}
              </label>
            )}
            {find.trim() && (
              <p className="mt-1 px-1 text-[12px] text-ink2">
                {shownLines.length === 0 ? `No line matches “${find.trim()}”.` : `${shownLines.length} of ${openLines.length} lines`}
              </p>
            )}
            <ul className="mt-1 divide-y divide-line rounded-xl border border-line">
              {shownLines.map((l) => (
                <li key={l.id}>
                  <button type="button" disabled={busy !== null} onClick={() => link(l)} className={cn('flex w-full items-center gap-3 px-3 py-2 text-left active:bg-sunken', busy === l.id && 'opacity-60')}>
                    <ProductImage src={l.image} alt="" className="h-10 w-10" />
                    <span className="min-w-0 flex-1">
                      <span className="clamp-2 block text-[14px] font-semibold leading-tight">{l.name}</span>
                      <span className="text-[12px] text-ink2">
                        {l.vendor_code && <span className="font-mono">{l.vendor_code}</span>}
                        {!isMultiBox(l) && l.boxes[0]?.vendor_barcode && <span className="font-mono tabular-nums">{l.vendor_code ? ' · ' : ''}EAN {l.boxes[0].vendor_barcode}</span>}
                        {isMultiBox(l) && `${l.vendor_code ? ' · ' : ''}${l.boxes.length} boxes`}
                        {l.boxes.length === 0 && `${l.vendor_code ? ' · ' : ''}no box on file yet`}
                      </span>
                    </span>
                    {busy === l.id ? <span className="text-[12px] text-ink2">Linking…</span> : <Link2 className="h-4 w-4 text-ink2" />}
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}

        {pickBoxFor && (
          <section className="mt-4">
            <button type="button" onClick={() => setPickBoxFor(null)} className="mb-1 inline-flex items-center gap-1 text-[13px] font-semibold text-ink2">
              <ChevronLeft className="h-4 w-4" /> {pickBoxFor.name}
            </button>
            <h3 className="text-[13px] font-semibold text-ink2">Which box is this?</h3>
            <ul className="mt-1 divide-y divide-line rounded-xl border border-line">
              {pickBoxFor.boxes.map((b, i) => (
                <li key={b.id}>
                  <button type="button" disabled={busy !== null} onClick={() => link(pickBoxFor, b)} className={cn('flex w-full items-center gap-3 px-3 py-2.5 text-left active:bg-sunken', busy === b.id && 'opacity-60')}>
                    <span className="min-w-0 flex-1">
                      <span className="block text-[14px] font-semibold">{boxLabel(b, i)}</span>
                      <span className="block text-[13px] text-ink2">{boxDims(b) || 'no size on file'}</span>
                      {b.vendor_barcode && <span className="block font-mono text-[12px] text-ink2">has {b.vendor_barcode}</span>}
                    </span>
                    {busy === b.id ? <span className="text-[12px] text-ink2">Linking…</span> : <Link2 className="h-4 w-4 text-ink2" />}
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}
          </>
        )}
      </div>
    </Sheet>
  );
}
